import { workspaceCandidateAssociationDecision } from "@moonshot/contracts/workspace-association";
import type { RequestTraceView, WorkspaceEntity } from "./types";

const fallbackBucketMinutes = 15;
const rotatingCrawlerMaximumGapMs = 75_000;
const rotatingCrawlerWindowMs = 4 * 60_000;
const rotatingCrawlerMinimumWindows = 5;
const rotatingCrawlerMinimumRoutes = 4;
const networkRegions: Record<string, {
  label: string;
  lat: number;
  lng: number;
  radiusKm: number;
}> = {
  AT: { label: "Österreich", lat: 47.6965, lng: 13.3457, radiusKm: 330 },
  DE: { label: "Deutschland", lat: 51.1657, lng: 10.4515, radiusKm: 520 },
  CH: { label: "Schweiz", lat: 46.8182, lng: 8.2275, radiusKm: 210 },
  LI: { label: "Liechtenstein", lat: 47.166, lng: 9.5554, radiusKm: 25 },
  BE: { label: "Belgien", lat: 50.6403, lng: 4.6667, radiusKm: 150 },
  NL: { label: "Niederlande", lat: 52.2435, lng: 5.6343, radiusKm: 190 },
  FR: { label: "Frankreich", lat: 46.2276, lng: 2.2137, radiusKm: 620 },
  GB: { label: "Vereinigtes Königreich", lat: 54.7024, lng: -3.2766, radiusKm: 650 },
  IE: { label: "Irland", lat: 53.1754, lng: -8.1604, radiusKm: 260 },
  IT: { label: "Italien", lat: 42.6384, lng: 12.6743, radiusKm: 650 },
  ES: { label: "Spanien", lat: 39.3262, lng: -4.8381, radiusKm: 720 },
  PT: { label: "Portugal", lat: 39.5572, lng: -7.8537, radiusKm: 390 },
  PL: { label: "Polen", lat: 52.2159, lng: 19.1345, radiusKm: 440 },
  CZ: { label: "Tschechien", lat: 49.7391, lng: 15.3315, radiusKm: 230 },
  SK: { label: "Slowakei", lat: 48.706, lng: 19.699, radiusKm: 210 },
  HU: { label: "Ungarn", lat: 47.1818, lng: 19.5061, radiusKm: 250 },
  SI: { label: "Slowenien", lat: 46.1199, lng: 14.8153, radiusKm: 120 },
  HR: { label: "Kroatien", lat: 45.5643, lng: 17.0119, radiusKm: 290 },
  DK: { label: "Dänemark", lat: 55.9397, lng: 9.5156, radiusKm: 220 },
  SE: { label: "Schweden", lat: 62.1983, lng: 17.5672, radiusKm: 780 },
  NO: { label: "Norwegen", lat: 64.5732, lng: 11.528, radiusKm: 950 },
  FI: { label: "Finnland", lat: 64.9842, lng: 26.096, radiusKm: 620 },
  US: { label: "Vereinigte Staaten", lat: 39.7837, lng: -100.4459, radiusKm: 2500 },
  CA: { label: "Kanada", lat: 61.0667, lng: -107.9917, radiusKm: 2600 },
  AU: { label: "Australien", lat: -24.7761, lng: 134.755, radiusKm: 2600 },
  NZ: { label: "Neuseeland", lat: -41.5001, lng: 172.8344, radiusKm: 850 },
  JP: { label: "Japan", lat: 36.5748, lng: 139.2394, radiusKm: 900 },
  SG: { label: "Singapur", lat: 1.3571, lng: 103.8195, radiusKm: 35 }
};
const regionNames = new Intl.DisplayNames(["de"], { type: "region" });
// The activity workspace represents real eligible HTML activity. Technical
// outcomes remain clearly labelled, but must not make an otherwise genuine
// page visit disappear from the normal activity feed.
const visibleTraceStatuses = new Set<RequestTraceView["status"]>([
  "candidate",
  "unknown",
  "suppressed",
  "provider_only",
  "hosting_or_vpn",
  "bot",
  "resolver_error",
  "stale_graph",
  "dropped_or_incomplete"
]);

const statusLabels: Record<RequestTraceView["status"], string> = {
  identified: "Identifiziert",
  verified_claim: "Verifizierter Network Claim",
  candidate: "Evidenzbasierte Firmenzuordnung",
  unknown: "Unbekannte Account-Aktivität",
  suppressed: "Unterdrückte Account-Aktivität",
  provider_only: "Provider-Netzwerk",
  hosting_or_vpn: "Hosting- oder VPN-Netzwerk",
  bot: "Automatisierter Request",
  not_eligible: "Nicht auswertbarer Request",
  resolver_error: "Account-Auflösung fehlgeschlagen",
  stale_graph: "Account-Auflösung mit veraltetem Graph",
  dropped_or_incomplete: "Unvollständige Account-Auflösung"
};

const routeCategoryLabels: Record<string, string> = {
  pricing: "Preise",
  contact: "Kontakt",
  case_study: "Referenzen",
  solution: "Lösungen",
  product: "Produkt",
  resources: "Ressourcen",
  company: "Unternehmen",
  legal: "Rechtliches",
  other: "Seite ohne Pfadlabel (älterer Connector)"
};
/** Approximate centre and extent of a country, for region-only locations. */
export function countryRegion(countryCode?: string) {
  return countryCode ? networkRegions[countryCode.trim().toUpperCase()] : undefined;
}

function workspaceEligibleTraceCandidate(
  candidate: RequestTraceView["candidates"][number] | undefined
) {
  return candidate && workspaceCandidateAssociationDecision(candidate).eligible
    ? candidate
    : undefined;
}

function routeCategoryLabel(category: string) {
  return routeCategoryLabels[category] ?? category.replaceAll("_", " ");
}

function traceRouteLabel(trace: RequestTraceView) {
  const explicit = trace.routeLabel?.trim();
  return explicit && !["weitere seite", "sonstiges", "other"].includes(explicit.toLowerCase())
    ? explicit
    : routeCategoryLabel(trace.routeCategory || "other");
}

function workspaceTraceCandidate(trace: RequestTraceView) {
  const explicitlySelected = trace.selectedOrganizationId
    ? trace.candidates.find((candidate) => candidate.organizationId === trace.selectedOrganizationId)
    : undefined;
  const eligibleSelection = workspaceEligibleTraceCandidate(explicitlySelected);
  if (eligibleSelection) return eligibleSelection;
  if (!["candidate", "unknown"].includes(trace.status)) return undefined;
  return [...trace.candidates]
    .sort((left, right) => right.score - left.score)
    .find((candidate) => workspaceEligibleTraceCandidate(candidate));
}

function hasKnownAppHostingProxySelectionBug(trace: RequestTraceView) {
  if (!/^2\.(?:[0-2]\.|3\.[01](?:\.|$))/.test(trace.connectorVersion)) return false;
  return trace.resolverSteps.some((step) =>
    step.name === "trusted-proxy"
    && step.summary?.includes("google-app-hosting-xff-pair; 3 valid forwarded hop(s)")
  );
}

function pipelineStatus(traces: RequestTraceView[]): WorkspaceEntity["reviewStatus"] {
  if (traces.some((trace) => trace.status === "candidate")) return "candidate";
  if (traces.every((trace) => ["suppressed", "provider_only", "hosting_or_vpn"].includes(trace.status))) return "suppressed";
  return "review";
}

function decisionDetail(trace: RequestTraceView) {
  return trace.suppressionReason
    ?? trace.unknownReason
    ?? (trace.pipelineStatus === "failed"
      ? "Die lokale Account-Auflösung konnte nicht abgeschlossen werden."
      : "Für diesen Request wurde keine veröffentlichbare Firma ermittelt.");
}

function fallbackBucketStart(trace: RequestTraceView) {
  const timestamp = new Date(trace.occurredAt).getTime();
  const bucketMs = fallbackBucketMinutes * 60_000;
  return Math.floor(timestamp / bucketMs) * bucketMs;
}

function fallbackCandidateKey(trace: RequestTraceView) {
  return trace.status === "candidate"
    ? workspaceTraceCandidate(trace)?.organizationId ?? trace.candidates[0]?.organizationId ?? "candidate"
    : "";
}

function activityGroupKey(trace: RequestTraceView) {
  if (trace.accountActivityWindowId && !hasKnownAppHostingProxySelectionBug(trace)) {
    return `window:${trace.accountActivityWindowId}`;
  }
  return [
    "bucket",
    fallbackBucketStart(trace),
    hasKnownAppHostingProxySelectionBug(trace) ? "legacy_proxy" : "normal",
    trace.status,
    trace.networkClass,
    trace.asn ?? "na",
    trace.networkCountryCode ?? "XX",
    fallbackCandidateKey(trace)
  ].join(":");
}

function isRotatingCrawlerTrace(trace: RequestTraceView) {
  const acquisition = trace.acquisitionCategory ?? "unknown";
  return (trace.status === "bot"
    && trace.suppressionReason?.includes("rotating activity-window crawl")
  )
    && trace.pipelineStatus === "complete"
    && trace.networkClass === "unknown"
    && !trace.asn
    && /^[A-Z]{2}$/.test(trace.networkCountryCode ?? "")
    && Boolean(trace.accountActivityWindowId)
    && trace.candidates.length === 0
    && !trace.acquisitionSource
    && ["direct", "unknown"].includes(acquisition)
    && Boolean(trace.routeLabel?.trim());
}

function rotatingCrawlerGroupKeys(traces: RequestTraceView[]) {
  const keys = new Map<string, string>();
  const countries = new Map<string, RequestTraceView[]>();
  traces.filter(isRotatingCrawlerTrace).forEach((trace) => {
    const country = trace.networkCountryCode!;
    const group = countries.get(country) ?? [];
    group.push(trace);
    countries.set(country, group);
  });
  const retainSegment = (country: string, segment: RequestTraceView[]) => {
    const activityWindows = new Set(segment.map((trace) => trace.accountActivityWindowId));
    const routes = new Set(segment.map(traceRouteLabel));
    if (
      segment.length < rotatingCrawlerMinimumWindows
      || activityWindows.size !== segment.length
      || routes.size < rotatingCrawlerMinimumRoutes
    ) return;
    const key = `crawl:${country}:${segment[0].requestTraceId}`;
    segment.forEach((trace) => keys.set(trace.requestTraceId, key));
  };
  countries.forEach((input, country) => {
    const ordered = [...input].sort((left, right) => left.occurredAt.localeCompare(right.occurredAt));
    let segment: RequestTraceView[] = [];
    ordered.forEach((trace) => {
      const occurredAt = new Date(trace.occurredAt).getTime();
      const previousAt = new Date(segment.at(-1)?.occurredAt ?? trace.occurredAt).getTime();
      const firstAt = new Date(segment[0]?.occurredAt ?? trace.occurredAt).getTime();
      if (segment.length && (
        occurredAt - previousAt > rotatingCrawlerMaximumGapMs
        || occurredAt - firstAt > rotatingCrawlerWindowMs
      )) {
        retainSegment(country, segment);
        segment = [];
      }
      segment.push(trace);
    });
    retainSegment(country, segment);
  });
  return keys;
}

function activityEntityId(groupKey: string) {
  if (groupKey.startsWith("window:")) return `trace-window-${groupKey.slice("window:".length)}`;
  return `trace-${groupKey.replaceAll(/[^a-zA-Z0-9_-]/g, "-")}`;
}

function confidenceLabel(score: number): WorkspaceEntity["confidenceLabel"] {
  if (score >= 0.98) return "very_high";
  if (score >= 0.9) return "high";
  if (score >= 0.64) return "medium";
  if (score >= 0.52) return "probable";
  if (score > 0) return "low";
  return "unknown";
}

export function isVisibleAccountTrace(trace: RequestTraceView) {
  return trace.mode === "account"
    && trace.consentStatus !== "accepted"
    && !trace.linkedVisitorProfileId
    && !hasKnownAppHostingProxySelectionBug(trace)
    && visibleTraceStatuses.has(trace.status);
}

function visitorToken(groupKey: string) {
  if (groupKey.startsWith("window:")) {
    const token = groupKey.slice("window:".length).replaceAll(/[^a-f0-9]/gi, "").slice(0, 6).toUpperCase();
    return token || "OFFEN";
  }
  let hash = 2_166_136_261;
  for (const character of groupKey) {
    hash ^= character.charCodeAt(0);
    hash = Math.imul(hash, 16_777_619);
  }
  return (hash >>> 0).toString(16).padStart(8, "0").slice(0, 6).toUpperCase();
}

function requestSourceFamily(traces: RequestTraceView[]) {
  for (const trace of [...traces].reverse()) {
    const parseStep = trace.resolverSteps.find((step) => step.name === "parse-ip");
    if (parseStep?.summary?.includes("IPv6")) return "IPv6";
    if (parseStep?.summary?.includes("IPv4")) return "IPv4";
  }
  return "";
}

function groupedAccountTraceActivity(groupKey: string, input: RequestTraceView[]): WorkspaceEntity {
  const traces = [...input].sort((left, right) => left.occurredAt.localeCompare(right.occurredAt));
  const latest = traces.at(-1)!;
  const strongest = [...traces].sort((left, right) => {
    const leftCandidate = workspaceTraceCandidate(left);
    const rightCandidate = workspaceTraceCandidate(right);
    return Number(Boolean(rightCandidate)) - Number(Boolean(leftCandidate))
      || (rightCandidate?.score ?? right.topScore) - (leftCandidate?.score ?? left.topScore)
      || right.occurredAt.localeCompare(left.occurredAt);
  })[0];
  const selectedCandidate = workspaceTraceCandidate(strongest);
  const requestCount = traces.length;
  const categoryCount = new Set(traces.map(traceRouteLabel)).size;
  const activityDurationMs = new Date(latest.occurredAt).getTime() - new Date(traces[0].occurredAt).getTime();
  const observedDurationMs = requestCount > 1 && activityDurationMs > 0
    ? activityDurationMs
    : 0;
  const rotatingCrawlerWindow = groupKey.startsWith("crawl:");
  // A privacy window may cover many employees. Route rate and country do not
  // independently establish automation; retain only an explicit resolver result.
  const automatedWindow = rotatingCrawlerWindow || traces.some((trace) =>
    trace.status === "bot"
    && (
      trace.suppressionReason?.includes("rapid multi-route crawl")
      || trace.suppressionReason?.includes("rotating activity-window crawl")
    )
  );
  const legacyProxySelection = traces.every(hasKnownAppHostingProxySelectionBug);
  const edgeWindow = rotatingCrawlerWindow ? undefined : traces.find((trace) =>
    trace.accountActivityWindowId && !hasKnownAppHostingProxySelectionBug(trace)
  );
  const networkTrace = [...traces].reverse().find((trace) =>
    !hasKnownAppHostingProxySelectionBug(trace)
    && (trace.networkLocation || trace.networkCountryCode || trace.asnName || trace.asn)
  ) ?? latest;
  const trustedCountryCode = legacyProxySelection ? undefined : networkTrace.networkCountryCode;
  const networkRegion = trustedCountryCode
    ? networkRegions[trustedCountryCode]
    : undefined;
  const location = !legacyProxySelection && networkTrace.networkLocation
    ? networkTrace.networkLocation
    : trustedCountryCode
    ? {
        label: `${networkRegion?.label ?? regionNames.of(trustedCountryCode) ?? trustedCountryCode} (Netzwerkregion)`,
        countryCode: trustedCountryCode,
        ...(networkRegion ? {
          lat: networkRegion.lat,
          lng: networkRegion.lng,
          radiusKm: networkRegion.radiusKm
        } : {}),
        precision: "country" as const
      }
    : {
        label: "Standort nicht bestimmbar",
        countryCode: "",
        precision: "unknown" as const
      };
  const exactWindow = Boolean(edgeWindow);
  const title = automatedWindow
    ? `Automated request ${visitorToken(groupKey)}`
    : `Anonymous activity ${visitorToken(groupKey)}`;
  const activityLevel = requestCount >= 5 ? "high" : requestCount >= 2 ? "medium" : "low";
  const windowMinutes = edgeWindow?.accountActivityWindowMinutes ?? fallbackBucketMinutes;
  const maximumObservableGapMs = windowMinutes * 60_000;
  const managedCollector = traces.some((trace) => trace.connectorVersion?.includes("-managed."));
  const groupingDetail = exactWindow
    ? managedCollector
      ? `${requestCount} Request(s) wurden im Moonshot Managed Collector in einem maximal ${windowMinutes} Minuten gültigen Aktivitätsfenster zusammengeführt. Die Raw-IP wurde nur flüchtig verarbeitet und nicht in Produktdaten gespeichert; persistiert wurde ausschließlich eine kryptografisch abgeleitete, strikt rotierende Window-ID. Das Fenster ist keine dauerhafte Personen-ID und kann bei gemeinsam genutzten Firmennetzen mehrere Personen umfassen.`
      : `${requestCount} Request(s) wurden am Kunden-Edge in einem maximal ${windowMinutes} Minuten gültigen Aktivitätsfenster zusammengeführt. Die Raw-IP blieb am Edge; Moonshot erhielt nur eine kryptografisch abgeleitete, strikt rotierende Window-ID. Das Fenster ist keine dauerhafte Personen-ID und kann bei gemeinsam genutzten Firmennetzen mehrere Personen umfassen.`
    : rotatingCrawlerWindow
      ? `${requestCount} einzelne, kurzlebige Aktivitätsfenster mit wechselnden Seiten wurden als zusammenhängender automatisierter Crawl erkannt. Die Erkennung nutzt nur Zeitabstand, Land, Window- und Routenvielfalt; weder Raw-IP noch Fingerprint oder User-Agent werden dafür korreliert.`
    : legacyProxySelection
      ? `${requestCount} Request(s) stammen aus Connector-Versionen mit einer inzwischen korrigierten App-Hosting-Proxy-Auswahl. Raw-IP-Daten wurden bewusst nicht gespeichert; deshalb kann die tatsächliche Netzwerkherkunft rückwirkend nicht rekonstruiert werden. Die Einträge bleiben als transparente historische Aktivität sichtbar, werden aber nicht als gemeinsame Besucheridentität ausgegeben.`
      : `${requestCount} ältere, technisch unverknüpfte Request(s) wurden ausschließlich für die Darstellung in einem ${fallbackBucketMinutes}-Minuten-Bucket verdichtet. Das ist kein Beleg, dass sie vom selben Besucher stammen.`;
  const latestCategory = traceRouteLabel(latest);
  const acquisitionTrace = traces.find((trace) =>
    trace.acquisitionCategory && trace.acquisitionCategory !== "internal"
  ) ?? [...traces].reverse().find((trace) => trace.acquisitionCategory);
  const sourceFamily = requestSourceFamily(traces);
  const networkOwner = networkTrace.asnName
    || (networkTrace.asn ? `AS${networkTrace.asn}` : "");
  const locationLabel = location.precision !== "unknown" ? location.label : "";
  const networkClassLabel = ({
    corporate: "Unternehmensnetz",
    provider: "Provider-Netz",
    hosting: "Hosting-/Rechenzentrumsnetz",
    vpn: "VPN-Netz",
    mobile: "Mobilfunknetz",
    residential: "Privatkundennetz",
    unknown: ""
  } as const)[networkTrace.networkClass];
  const unresolvedNetworkLabel = [networkClassLabel, sourceFamily ? `öffentliches ${sourceFamily}-Netz` : ""]
    .filter(Boolean)
    .join(" · ");
  const activityOriginLabel = [networkOwner, locationLabel].filter(Boolean).join(" · ")
    || `${unresolvedNetworkLabel || "Öffentliches Netz"} · Herkunft noch nicht im Graph`;
  const activityOriginDetail = locationLabel
    ? managedCollector
      ? `Die Herkunft wurde im Managed Collector als ${locationLabel}${networkOwner ? ` über ${networkOwner}` : ""} eingeordnet. Die Raw-IP wurde flüchtig verarbeitet und nicht in Produktdaten gespeichert.`
      : `Die Herkunft wurde lokal am Kunden-Edge als ${locationLabel}${networkOwner ? ` über ${networkOwner}` : ""} eingeordnet. Die Raw-IP wurde nicht an Moonshot übertragen.`
    : `${sourceFamily ? `Die Request-Quelle war eine gültige ${sourceFamily}-Adresse, ` : "Die Request-Quelle wurde am Resolver verarbeitet, "}aber der aktive Netzwerkgraph enthielt noch kein passendes Herkunftspräfix. Die Raw-IP wurde nicht in Produktdaten gespeichert.`;
  return {
    id: activityEntityId(groupKey),
    kind: "account",
    mode: "account",
    organizationId: selectedCandidate?.organizationId,
    requestTraceId: latest.requestTraceId,
    requestTraceIds: traces.map((trace) => trace.requestTraceId),
    traceActivity: true,
    activityWindowId: edgeWindow?.accountActivityWindowId,
    activityAggregation: exactWindow ? "edge_window" : "time_bucket",
    activityRequestCount: requestCount,
    activityCategoryCount: categoryCount,
    activityStatus: automatedWindow ? "bot" : strongest.status,
    activityStatusLabel: legacyProxySelection
      ? "Historische Proxy-Auswertung"
      : automatedWindow
        ? "Automatisierter Seitenabruf"
        : selectedCandidate
        ? selectedCandidate.score >= 0.52 && selectedCandidate.score < 0.64 ? "Wahrscheinlich erkannt" : "Evidenzbasierte Firmenzuordnung"
        : strongest.status === "candidate" ? "Firma noch nicht bestimmbar" : statusLabels[strongest.status],
    activityCandidateCount: strongest.candidates.filter(candidate => workspaceCandidateAssociationDecision(candidate).eligible).length,
    activityTopCandidateName: selectedCandidate?.name,
    activityOriginLabel,
    activityOriginDetail,
    ...(location.precision !== "unknown" ? { activityOriginLocation: location } : {}),
    detailsLoaded: true,
    visitorLabel: title,
    companyName: selectedCandidate?.name ?? title,
    legalName: selectedCandidate?.name,
    domain: selectedCandidate?.domain,
    legalEntityKind: "unknown",
    employeeBand: "unknown",
    consent: { status: latest.consentStatus, purposes: [] },
    confidence: selectedCandidate?.score ?? 0,
    confidenceLabel: selectedCandidate ? confidenceLabel(selectedCandidate.score) : "unknown",
    live: !automatedWindow && Date.now() - new Date(latest.occurredAt).getTime() <= 2 * 60_000,
    lastSeenAt: latest.occurredAt,
    firstSeenAt: traces[0].occurredAt,
    intentCategory: latestCategory,
    acquisition: acquisitionTrace?.acquisitionCategory,
    acquisitionSource: acquisitionTrace?.acquisitionSource,
    activityLevel,
    sessions: 1,
    engagementMs: observedDurationMs,
    engagementMeasurement: observedDurationMs > 0 ? "measured" : "unmeasured",
    engagementBasis: "observed_request_span",
    visitOccurrences: [{
      id: edgeWindow?.accountActivityWindowId ?? groupKey,
      startedAt: traces[0].occurredAt,
      lastSeenAt: latest.occurredAt,
      title: latestCategory,
      sessionNumber: 1,
      returning: false,
      acquisition: acquisitionTrace?.acquisitionCategory,
      acquisitionSource: acquisitionTrace?.acquisitionSource,
      visitorLabel: title,
      requestTraceId: latest.requestTraceId,
      engagementMs: observedDurationMs,
      engagementMeasurement: observedDurationMs > 0 ? "measured" : "unmeasured"
    }],
    targetAccount: false,
    reviewStatus: pipelineStatus(traces),
    location,
    journey: traces.map((trace, index) => {
      const occurredAt = new Date(trace.occurredAt).getTime();
      const nextOccurredAt = traces[index + 1]
        ? new Date(traces[index + 1].occurredAt).getTime()
        : Number.NaN;
      const observableGapMs = nextOccurredAt - occurredAt;
      return {
        id: trace.requestTraceId,
        sessionId: edgeWindow?.accountActivityWindowId,
        occurredAt: trace.occurredAt,
        type: "intent_bucket" as const,
        title: traceRouteLabel(trace),
        category: traceRouteLabel(trace),
        ...(Number.isFinite(observableGapMs)
          && observableGapMs > 0
          && observableGapMs <= maximumObservableGapMs
          ? { durationMs: observableGapMs }
          : {})
      };
    }),
    evidence: selectedCandidate?.evidence ?? [],
    network: {
      ...(!legacyProxySelection && networkTrace.asn ? { asn: networkTrace.asn } : {}),
      ...(!legacyProxySelection && networkTrace.asnName ? {
        asnName: networkTrace.asnName,
        directOwner: networkTrace.asnName
      } : {}),
      networkClass: legacyProxySelection ? "unknown" : networkTrace.networkClass
    },
    resolutionStory: [
      {
        label: exactWindow ? "Kurzlebiges Edge-Aktivitätsfenster" : "Historisches Darstellungsaggregat",
        detail: groupingDetail,
        status: "complete"
      },
      {
        label: legacyProxySelection ? "Proxy-Auswahl korrigiert" : selectedCandidate ? "Firmenhinweis geprüft" : "Keine veröffentlichte Firma",
        detail: legacyProxySelection
          ? "Die damalige Auswertung traf einen Google-Infrastruktur-Hop statt der ursprünglichen Request-Quelle. Connector 2.3.2 verwendet die verifizierte dreistufige App-Hosting-Kette. Dieser historische Datensatz wird deshalb nicht für Standort- oder Firmenaussagen verwendet."
          : decisionDetail(strongest),
        status: "warning"
      },
      {
        label: exactWindow ? "Keine dauerhafte Wiedererkennung" : "Keine Besucheridentität",
        detail: exactWindow
          ? "Die Window-ID läuft automatisch ab und kann weder über Zeitfenster hinweg noch über Tage als Besucherprofil verwendet werden."
          : "Ohne frühere Window-ID können diese Requests rückwirkend nicht seriös zu einer individuellen Journey verbunden werden.",
        status: "warning"
      }
    ]
  };
}

export function accountTraceActivity(trace: RequestTraceView): WorkspaceEntity | null {
  return isVisibleAccountTrace(trace)
    ? groupedAccountTraceActivity(activityGroupKey(trace), [trace])
    : null;
}

export function accountTraceActivities(traces: RequestTraceView[]) {
  const groups = new Map<string, RequestTraceView[]>();
  // First request per window that belongs to a consented visitor. Earlier
  // unlinked requests are that browser before consent (already represented by
  // the visitor). Later unlinked requests come from another browser or person
  // on the same network and stay visible as their own anonymous activity.
  const firstLinkedAt = new Map<string, string>();
  traces.forEach((trace) => {
    if (!trace.linkedVisitorProfileId || !trace.accountActivityWindowId) return;
    const previous = firstLinkedAt.get(trace.accountActivityWindowId);
    if (!previous || trace.occurredAt < previous) firstLinkedAt.set(trace.accountActivityWindowId, trace.occurredAt);
  });
  const visibleTraces = traces.filter((candidate) => {
    if (!isVisibleAccountTrace(candidate)) return false;
    const linkedAt = candidate.accountActivityWindowId ? firstLinkedAt.get(candidate.accountActivityWindowId) : undefined;
    return !linkedAt || candidate.occurredAt > linkedAt;
  });
  const crawlerGroupKeys = rotatingCrawlerGroupKeys(visibleTraces);
  for (const trace of visibleTraces) {
    const key = crawlerGroupKeys.get(trace.requestTraceId) ?? activityGroupKey(trace);
    const group = groups.get(key) ?? [];
    group.push(trace);
    groups.set(key, group);
  }
  return [...groups.entries()].map(([key, group]) => groupedAccountTraceActivity(key, group));
}

export function synchronizeAccountTraceActivities(
  entities: WorkspaceEntity[],
  traces: RequestTraceView[]
) {
  const retained = entities.filter((entity) => !entity.traceActivity);
  const representedTraceIds = new Set(retained.flatMap((entity) => [
    entity.requestTraceId,
    ...(entity.requestTraceIds ?? []),
    ...(entity.visitOccurrences ?? []).map((occurrence) => occurrence.requestTraceId)
  ].filter((value): value is string => Boolean(value))));
  const traceActivities = accountTraceActivities(traces).filter((entity) => {
    const traceIds = [entity.requestTraceId, ...(entity.requestTraceIds ?? [])]
      .filter((value): value is string => Boolean(value));
    return !traceIds.some((traceId) => representedTraceIds.has(traceId));
  });
  return [
    ...retained,
    ...traceActivities
  ];
}
